import { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { useDomains } from "@/context/DomainContext";
import { PageHeader, NoDomain, EmptyState } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Smartphone, Loader2, MoreVertical, ShieldX, Ban, CheckCircle2, Trash2, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const ACTIONS = [
  { action: "approve", label: "Approve", icon: CheckCircle2 },
  { action: "block", label: "Block", icon: Ban },
  { action: "admin_account_wipe", label: "Account wipe", icon: ShieldX },
  { action: "admin_remote_wipe", label: "Full remote wipe", icon: ShieldX },
];

export default function Devices() {
  const { selected } = useDomains();
  const did = selected?.id;
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!did) return; setLoading(true);
    try { const { data } = await api.get(`/domains/${did}/devices`); setDevices(data); }
    catch (e) { toast.error(apiError(e)); setDevices([]); } finally { setLoading(false); }
  }, [did]);
  useEffect(() => { load(); }, [load]);

  if (!selected) return <NoDomain />;
  const doAction = async (d, action) => {
    if (action.includes("wipe") && !window.confirm(`${action} on ${d.model}? This cannot be undone.`)) return;
    try { await api.post(`/domains/${did}/devices/${d.resourceId}/action`, { action }); toast.success(`${action} sent`); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const del = async (d) => { if (!window.confirm("Remove device from management?")) return; try { await api.delete(`/domains/${did}/devices/${d.resourceId}`); toast.success("Removed"); load(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div className="animate-fade-up">
      <PageHeader title="Mobile Devices" subtitle={selected.domain} icon={Smartphone}
        actions={<Button variant="outline" className="rounded-xl bg-white/60" onClick={load} data-testid="refresh-devices"><RefreshCw className="mr-1 h-4 w-4" />Refresh</Button>} />
      <div className="glass rounded-3xl p-4">
        {loading ? <div className="flex justify-center p-10 text-emerald-600"><Loader2 className="h-6 w-6 animate-spin" /></div> :
          devices.length === 0 ? <EmptyState label="No managed devices" /> :
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400">
                <th className="px-3 py-2">Device</th><th className="px-3 py-2">User</th><th className="px-3 py-2">Status</th><th className="px-3 py-2 text-right">Actions</th></tr></thead>
              <tbody>
                {devices.map((d) => (
                  <tr key={d.resourceId} className="border-b border-white/60 hover:bg-white/50" data-testid={`device-${d.resourceId}`}>
                    <td className="px-3 py-3"><p className="font-medium text-slate-800">{d.model || d.name}</p><p className="text-xs text-slate-500">{d.os} · {d.type}</p></td>
                    <td className="px-3 py-3 text-slate-600">{d.email}</td>
                    <td className="px-3 py-3"><Badge className={d.status === "APPROVED" ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-600"}>{d.status}</Badge></td>
                    <td className="px-3 py-3 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg" data-testid={`device-menu-${d.resourceId}`}><MoreVertical className="h-4 w-4" /></Button></DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {ACTIONS.map((a) => <DropdownMenuItem key={a.action} onClick={() => doAction(d, a.action)}><a.icon className="mr-2 h-4 w-4" />{a.label}</DropdownMenuItem>)}
                          <DropdownMenuItem className="text-rose-600" onClick={() => del(d)}><Trash2 className="mr-2 h-4 w-4" />Delete device</DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>}
      </div>
    </div>
  );
}
